import { Action } from 'redux';
import { type ThunkDispatch } from 'redux-thunk';
import { Observable } from 'rxjs';
import { RootState } from 'src/root/store';

import type {
  PostAddAdultsToSubUnitRequest,
  PostAddSubUnitRequest,
  PostAddYouthsToSubUnitRequest,
  PutSubUnitRequest,
} from '@appTypes/esb';
import { denChiefApi } from '@modules/advancement/api';
import {
  FormatedDenChiefToRemove,
  FormattedSubUnitDenChief,
} from '@modules/advancement/utilsTyped';
import { esbApi } from '@modules/rtk/esb-api';

/**
 * @esbEndpoint GET /organizations/v2/units/:organizationGuid/subUnits
 */
const getSubUnits$ = (
  organizationGuid: string,
  forceRefetch: boolean,
  dispatch: ThunkDispatch<RootState, void, Action>,
) => {
  const thunk = dispatch(
    esbApi.endpoints.getSubUnits.initiate(
      {
        orgGuid: organizationGuid,
      },
      { forceRefetch },
    ),
  ).then(result => {
    if (result.error) {
      // @ts-expect-error: ignor status type
      const errorStatus: number = result.error.status;
      if (errorStatus !== 400 && errorStatus !== 404 && errorStatus !== 401) {
        throw result;
      }

      return [];
    }
    return result.data;
  });

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/add
 */
const createSubUnit$ = ({
  organizationGuid,
  subUnitType,
  subUnitData,
  dispatch,
}: {
  organizationGuid: string;
  subUnitType: string;
  subUnitData: PostAddSubUnitRequest;
  dispatch: ThunkDispatch<RootState, void, Action>;
}): Observable<{ id: number }> => {
  const thunk = dispatch(
    esbApi.endpoints.createSubUnit.initiate({
      orgGuid: organizationGuid,
      subUnitType,
      body: subUnitData,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId
 */
const deleteSubUnit$ = (
  organizationGuid: string,
  subUnitType: string,
  subUnitId: number,
  dispatch: ThunkDispatch<RootState, void, Action>,
): Observable<unknown> => {
  const thunk = dispatch(
    esbApi.endpoints.deleteSubUnit.initiate({
      orgGuid: organizationGuid,
      subUnitType,
      subUnitId,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/youths
 */
const addSubUnitYouths$ = (
  organizationGuid: string,
  subUnitType: string,
  subUnitId: number,
  data: PostAddYouthsToSubUnitRequest,
  dispatch: ThunkDispatch<RootState, void, Action>,
): Observable<{ message: string }> => {
  const thunk = dispatch(
    esbApi.endpoints.addYouthsToSubUnit.initiate({
      orgGuid: organizationGuid,
      subUnitType,
      subUnitId,
      body: data,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/adults
 */
const addSubUnitAdults$ = (
  organizationGuid: string,
  subUnitType: string,
  subUnitId: number,
  data: PostAddAdultsToSubUnitRequest,
  dispatch: ThunkDispatch<RootState, void, Action>,
): Observable<{ message: string }> => {
  const thunk = dispatch(
    esbApi.endpoints.addAdultsToSubUnit.initiate({
      orgGuid: organizationGuid,
      subUnitType,
      subUnitId,
      body: data,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint POST /organizations/v2/:organizationGuid/denChief/add
 */
const addSubUnitDenChiefs$ = (
  organizationGuid: string,
  data: FormattedSubUnitDenChief,
  dispatch: ThunkDispatch<RootState, void, Action>,
): Observable<{ message: string }> => {
  const thunk = dispatch(
    denChiefApi.endpoints.addDenChief.initiate({
      orgGuid: organizationGuid,
      payload: data,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint PUT /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId
 */
const editSubUnit$ = ({
  organizationGuid,
  subUnitType,
  subUnitId,
  subUnitData,
  dispatch,
}: {
  organizationGuid: string;
  subUnitType: string;
  subUnitId: number;
  subUnitData: PutSubUnitRequest;
  dispatch: ThunkDispatch<RootState, void, Action>;
}) => {
  const thunk = dispatch(
    esbApi.endpoints.UpdateSubUnit.initiate({
      orgGuid: organizationGuid,
      subUnitType,
      subUnitId,
      body: subUnitData,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/users
 */
const deleteSubUnitUsers$ = (
  organizationGuid: string,
  subUnitType: string,
  subUnitId: number,
  usersToDelete: number[],
  dispatch: ThunkDispatch<RootState, void, Action>,
) => {
  const thunk = dispatch(
    esbApi.endpoints.deleteUserFromSubUnit.initiate({
      orgGuid: organizationGuid,
      subUnitType,
      subUnitId,
      usersToDelete,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

/**
 * @esbEndpoint POST /organizations/v2/:organizationGuid/denChief/remove
 */
const removeSubUnitDenChief$ = (
  organizationGuid: string,
  data: FormatedDenChiefToRemove,
  dispatch: ThunkDispatch<RootState, void, Action>,
): Observable<{ message: string }> => {
  const thunk = dispatch(
    denChiefApi.endpoints.removeDenChief.initiate({
      orgGuid: organizationGuid,
      payload: data,
    }),
  )
    .unwrap()
    .then(result => result);

  return Observable.fromPromise(thunk);
};

export default {
  getSubUnits$,
  createSubUnit$,
  deleteSubUnit$,
  addSubUnitYouths$,
  addSubUnitAdults$,
  addSubUnitDenChiefs$,
  editSubUnit$,
  deleteSubUnitUsers$,
  removeSubUnitDenChief$,
};
